import { useEffect, useState } from 'react';

import { useAuth } from 'contexts/AuthContext';
import { organizationAdmin } from 'services/api';
import type { OrganizationAdminScope } from 'services/api/organizationAdminClient';
import type { OrganizationAdminOverviewData } from 'types';

const VIEWER_ORGANIZATION_CACHE_KEY = 'viewer-organization';
const cachedOverviews = new Map<string, OrganizationAdminOverviewData>();
const pendingOverviewRequests = new Map<string, Promise<OrganizationAdminOverviewData>>();

const getScopeCacheKey = (scope?: OrganizationAdminScope) => (
  scope?.organizationId ? `organization:${scope.organizationId}` : VIEWER_ORGANIZATION_CACHE_KEY
);

const loadOrganizationAdminOverview = async (
  scope: OrganizationAdminScope | undefined,
  cacheKey: string,
  { refresh = false }: { refresh?: boolean } = {},
) => {
  const cachedOverview = cachedOverviews.get(cacheKey);
  if (cachedOverview && !refresh) {
    return cachedOverview;
  }

  let pendingOverviewRequest = pendingOverviewRequests.get(cacheKey);
  if (!pendingOverviewRequest) {
    pendingOverviewRequest = (scope ? organizationAdmin.getOverview(scope) : organizationAdmin.getOverview())
      .then((data) => {
        cachedOverviews.set(cacheKey, data);
        return data;
      })
      .finally(() => {
        pendingOverviewRequests.delete(cacheKey);
      });
    pendingOverviewRequests.set(cacheKey, pendingOverviewRequest);
  }

  return pendingOverviewRequest;
};

export const clearOrganizationAdminOverviewCache = () => {
  cachedOverviews.clear();
  pendingOverviewRequests.clear();
};

export const useOrganizationAdminOverview = (
  fallbackErrorMessage: string,
  scope?: OrganizationAdminScope,
) => {
  const { user } = useAuth();
  // Keyed per signed-in user so a later sign-in in the same tab never sees another admin's data.
  const cacheKey = `${user?.id ?? 'anonymous'}:${getScopeCacheKey(scope)}`;
  const [overview, setOverview] = useState<OrganizationAdminOverviewData | null>(
    () => cachedOverviews.get(cacheKey) ?? null,
  );
  const [isLoading, setIsLoading] = useState(() => !cachedOverviews.has(cacheKey));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const cachedOverview = cachedOverviews.get(cacheKey);

    if (cachedOverview) {
      setOverview(cachedOverview);
      setIsLoading(false);
      setError(null);
      // Show the cached copy immediately, then refresh it so returning admins see current data.
      loadOrganizationAdminOverview(scope, cacheKey, { refresh: true })
        .then((data) => {
          if (isMounted) setOverview(data);
        })
        .catch((loadError) => {
          console.error('Failed to refresh organization admin overview:', loadError);
        });
      return () => {
        isMounted = false;
      };
    }

    setOverview(null);
    setIsLoading(true);
    setError(null);
    loadOrganizationAdminOverview(scope, cacheKey)
      .then((data) => {
        if (isMounted) {
          setOverview(data);
        }
      })
      .catch((loadError) => {
        console.error('Failed to load organization admin overview:', loadError);
        if (isMounted) {
          setError(fallbackErrorMessage);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [cacheKey, fallbackErrorMessage, scope]);

  return { overview, isLoading, error };
};
